import React, {useCallback, useEffect, useMemo} from 'react';
import {cn} from '@/helpers/shadcn/utils';
import {cva} from 'class-variance-authority';
import {Icon, Button} from '@/components/atoms';
import {COLOR_CODE} from '@/utils/constants/colors';
import {ICON_TYPES} from '@/components/common/common';
import PropTypes from 'prop-types';

/**
 * Alert component to display different types of alert messages.
 *
 * @param {Object} props - The properties object.
 * @param {'success' | 'error' | 'info' | 'warning'} props.type - The type of alert to display.
 * @param {Object} props.message - The message object containing title, subTitle, and buttonText.
 * @param {string} [props.message.title] - The title of the alert.
 * @param {string} [props.message.subTitle] - The subtitle/content of the alert.
 * @param {string} [props.message.buttonText] - The text for the action button.
 * @param {boolean} [props.show=false] - Whether the alert is visible or not.
 * @param {Function} [props.onCloseClick] - The function to call when the close button is clicked.
 * @param {number} [props.duration=5000] - Auto-close duration in milliseconds.
 * @param {Function} [props.handleClick] - The function to call when the action button is clicked.
 * @param {string} [props.iconTrailing] - The trailing icon for the button.
 *
 * @returns {JSX.Element|null} The Alert component or null if not visible.
 */
const DuplicateAlert = ({
  type = 'info',
  message,
  show = false,
  onCloseClick,
  duration = 5000,
  handleClick,
  iconTrailing
}) => {
  useEffect(() => {
    if (!show) return;
    // Auto-close functionality can be uncommented if needed
    // const timer = setTimeout(() => onCloseClick?.(), duration)
    // return () => clearTimeout(timer)
  }, [message, onCloseClick, show, type, duration]);

  // Type-based styling configuration
  const typeStyles = useMemo(
    () => ({
      info: {
        backgroundColor: COLOR_CODE._00152B,
        borderColor: COLOR_CODE._0073E6,
        buttonBackground: null,
        buttonBorderColor: null
      },
      warning: {
        backgroundColor: COLOR_CODE._CC6200,
        borderColor: COLOR_CODE._CC6200,
        buttonBackground: COLOR_CODE._CC6200,
        buttonBorderColor: COLOR_CODE._FFFFFF
      },
      caution: {
        backgroundColor: '#421703',
        borderColor: '#E5A408',
        buttonBackground: '#E5A408',
        buttonBorderColor: COLOR_CODE._FFFFFF
      },
      success: {
        backgroundColor: COLOR_CODE._00152B,
        borderColor: COLOR_CODE._0073E6,
        buttonBackground: COLOR_CODE._00A651,
        buttonBorderColor: COLOR_CODE._FFFFFF
      },
      error: {
        backgroundColor: COLOR_CODE._00152B,
        borderColor: COLOR_CODE._0073E6,
        buttonBackground: COLOR_CODE._DB3333,
        buttonBorderColor: COLOR_CODE._FFFFFF
      }
    }),
    []
  );

  const alertVariants = cva(
    `relative flex items-start w-full rounded-md px-4 py-2 alert max-w-[calc(100vw-7.5rem)]`,
    {
      variants: {
        type: {
          success: 'bg-support-success-primary text-white',
          error: 'bg-support-error-primary text-white',
          info: 'bg-support-info-primary text-white',
          warning: 'bg-[#CC6200]  text-white',
          caution: 'bg-[#421703] text-white'
        }
      }
    }
  );

  // Get the current type's styles or default to info
  const currentStyles = useMemo(
    () => typeStyles[type] || typeStyles.info,
    [type, typeStyles]
  );

  // Build button style with conditional background
  const buttonStyle = useMemo(() => {
    return currentStyles.buttonBackground
      ? {
          backgroundColor: currentStyles.buttonBackground,
          border: `1px solid ${currentStyles.buttonBorderColor}`
        }
      : {};
  }, [currentStyles]);

  // Icon container class - different sizes for warning vs others
  const iconContainerClass = useMemo(() => {
    return type === 'warning'
      ? 'inline-flex h-8 w-8 items-center justify-center rounded-lg -mr-6'
      : 'inline-flex h-6 w-6 items-center justify-center rounded-lg -mr-6';
  }, [type]);

  const renderMessageContent = useCallback(() => {
    if (!message) return null;

    return (
      <div className='flex items-center justify-between text-sm text-white'>
        <div className='flex-1'>
          {message.title && (
            <div className='mb-1 font-bold'>{message.title}</div>
          )}
          {message.subTitle && <div>{message.subTitle}</div>}
        </div>

        {message.buttonText && (
          <Button
            handleButton={handleClick}
            style={buttonStyle}
            uiSchema={{
              className:
                'ml-4 mt-1 inline-flex items-center justify-center text-icon',
              title: message?.buttonText,
              size: 'medium',
              variant: 'primary',
              iconTrailing: iconTrailing
            }}
          />
        )}
      </div>
    );
  }, [message, handleClick, buttonStyle, iconTrailing]);

  if (!show) return null;

  return (
    <div
      data-testid={`alert-${type}`}
      className={cn(alertVariants({type}), 'my-4 gap-4')}
      role='alert'
      style={{
        backgroundColor: currentStyles.backgroundColor,
        border: `1px solid ${currentStyles.borderColor}`
      }}>
      {/* Icon Section */}
      <div className={iconContainerClass}>
        <Icon
          icon={ICON_TYPES[type]}
          size='medium'
          className='text-button-icon'
        />
      </div>

      {/* Content Section */}
      <div className='text-md ml-3 flex-1 items-center py-0'>
        {renderMessageContent()}
      </div>
    </div>
  );
};

DuplicateAlert.propTypes = {
  type: PropTypes.oneOf(['success', 'error', 'info', 'warning', 'caution'])
    .isRequired,
  message: PropTypes.shape({
    title: PropTypes.string,
    subTitle: PropTypes.string,
    buttonText: PropTypes.string
  }).isRequired,
  show: PropTypes.bool,
  onCloseClick: PropTypes.func,
  duration: PropTypes.number,
  handleClick: PropTypes.func,
  iconTrailing: PropTypes.string
};

export default React.memo(DuplicateAlert);
